/**
 * @file audit-report/sampling-forms.ts
 * @description 採樣送檢「樣品分類 → 表單模板 code」映射（序72）。
 *
 * 採樣送檢分兩步（序72：拆「新增採樣」/「新增送檢」）：
 *  - 【新增採樣】按 scheme + 樣品分類選「採樣單」模板；
 *  - 【新增送檢】按 scheme 選「送檢單」模板（送檢單不分樣品分類）。
 *
 * 樣品分類（sampleCategory，序72）：
 *  - product     產品
 *  - water_soil  水、土壤（介質）
 *  - honey       蜂蜜產品
 *
 * 採樣單映射（序72）：
 *  | scheme | product      | water_soil  | honey        |
 *  | ------ | ------------ | ----------- | ------------ |
 *  | TOP    | D-ZHSR03-6   | D-ZHSR03-7  | （無，回退 D-ZHSR03-6 並提示）|
 *  | TAP    | TAP-ZHSR03-2 | D-ZHSR03-3  | TAP-ZHSR03-10 |
 *  | ECO    | 暫回退 TOP（尚無 ECO 專屬採樣單）                          |
 *
 * 送檢單映射（序72）：TOP=D-ZHSR03-5、TAP=TAP-ZHSR03-1；ECO 暫回退 TOP。
 *
 * ⚠️ 蜂蜜（honey）僅 TAP（產銷履歷）有專屬採樣單（TAP-ZHSR03-10）；
 *   TOP（有機）無蜂蜜採樣單，選 honey+TOP 時回退產品採樣單 D-ZHSR03-6，
 *   並由 `resolveSamplingTemplate` 回傳 `fallback: true` 供前端提示。
 *
 * 本檔為純函式（不接 DB / 前後端共用），供：
 *  - 前端 SamplesPanel 依 scheme + 分類決定要建哪張採樣單；
 *  - 後端 createSample / 送檢端點依同一映射建 form_instance。
 */
/** 樣品分類（序72：採樣單樣品分類三選一） */
export const SAMPLE_CATEGORIES = ['product', 'water_soil', 'honey'];
/** 樣品分類中文標籤（前端下拉展示用） */
export const SAMPLE_CATEGORY_LABELS = {
    product: '產品',
    water_soil: '水、土壤',
    honey: '蜂蜜產品',
};
/**
 * 採樣單模板映射（scheme → 分類 → 模板 code）。
 * ECO 暫無專屬採樣單，於 `resolveSamplingTemplate` 內回退 TOP。
 */
const SAMPLING_TEMPLATE_MAP = {
    TOP: {
        product: 'D-ZHSR03-6',
        water_soil: 'D-ZHSR03-7',
        // TOP（有機）無蜂蜜專屬採樣單 → null，resolveSamplingTemplate 回退產品採樣單並標記 fallback
        honey: null,
    },
    TAP: {
        product: 'TAP-ZHSR03-2',
        water_soil: 'D-ZHSR03-3',
        honey: 'TAP-ZHSR03-10',
    },
};
/** 送檢單模板映射（scheme → 模板 code；不分樣品分類）。ECO 暫回退 TOP。 */
const SUBMISSION_TEMPLATE_MAP = {
    TOP: 'D-ZHSR03-5',
    TAP: 'TAP-ZHSR03-1',
};
/**
 * 把 scheme 正規化到有採樣單定義的基準 scheme。
 * ECO 尚無專屬採樣單，統一回退 TOP（有機）。
 */
function baseScheme(scheme) {
    return scheme === 'TAP' ? 'TAP' : 'TOP';
}
/**
 * 依 scheme + 樣品分類解析採樣單模板 code。
 *
 * @param scheme   驗證方案（TOP / TAP / ECO；ECO 回退 TOP）
 * @param category 樣品分類（product / water_soil / honey）
 * @returns 模板 code 與是否回退旗標；映射缺漏時回退產品採樣單並標記 fallback。
 */
export function resolveSamplingTemplate(scheme, category) {
    const base = baseScheme(scheme);
    const code = SAMPLING_TEMPLATE_MAP[base][category];
    if (code)
        return { templateCode: code, fallback: false };
    // 該分類在此 scheme 無專屬採樣單（目前僅 TOP+honey）→ 回退產品採樣單
    return { templateCode: SAMPLING_TEMPLATE_MAP[base].product ?? 'D-ZHSR03-6', fallback: true };
}
/**
 * 依 scheme 解析送檢單模板 code（送檢單不分樣品分類）。
 *
 * @param scheme 驗證方案（TOP / TAP / ECO；ECO 回退 TOP）
 * @returns 送檢單模板 code。
 */
export function resolveSubmissionTemplate(scheme) {
    return SUBMISSION_TEMPLATE_MAP[baseScheme(scheme)];
}
